import { ArrowPathIcon, ExclamationTriangleIcon, InboxStackIcon } from "@heroicons/react/24/outline";
import { monthKeysForecastEnum } from "../../consts/quickbooksReports";
import { useDispatch, useSelector } from "react-redux";
import { partialUpdateForecastPlanningRequest } from "../../slices/quickbooks/forecastPlanningSlice";
import { monthsLowerCase } from "../../consts/forecast";
import { useEffect, useMemo, useState } from "react";
import { formatPrice } from "../../helpers/common";
import ReactTooltip from "react-tooltip";

export const ForecastPlanningMonthlyAllocation = ({
    monthsData,
    annualGoal,
    forecastPlanningId,
    lastType,
    access,
    onSave
}) => {
    const dispatch = useDispatch();
    const forecastPlanningState = useSelector((state) => state.forecastPlanning);


    const [monthlyData, setMonthlyData] = useState(monthsData || {});

    const totalPercentage = Math.round((Object.values(monthlyData).reduce((acc, val) => acc + Number(val || 0), 0) + Number.EPSILON) * 100) / 100;
    const projectedRevenue = (Number(annualGoal || 0) * totalPercentage) / 100;
    // keep local state in sync when parent-provided data changes (e.g., after save or type switch)
    useEffect(() => {
        setMonthlyData(monthsData || {});
    }, [monthsData]);

    const totals = useMemo(() => {
        const values = Object.values(monthlyData || {});
        const sum = values.reduce((acc, v) => acc + Number(v || 0), 0);
        const rounded = Math.round(sum * 10) / 10;
        return { sum, rounded };
    }, [monthlyData]);

    const isDirty = useMemo(() => {
        const keys = Object.keys(monthsData || {});
        if (keys.length !== Object.keys(monthlyData || {}).length) return true;
        for (const key of keys) {
            const a = Number((monthsData || {})[key] ?? 0);
            const b = Number((monthlyData || {})[key] ?? 0);
            if (a !== b) return true;
        }
        return false;
    }, [monthsData, monthlyData]);

    const handleChange = (key) => (e) => {
        const next = e.target.value;
        const numeric = next === "" ? "" : Number(next);
        setMonthlyData((prev) => ({
            ...prev,
            [key]: isNaN(numeric) ? 0 : numeric
        }));
    };

    const showWarning = totals.rounded !== 100;
    const warningText = totals.rounded > 100
        ? `Total exceeds 100% by ${(Math.round((totals.rounded - 100) * 10) / 10).toFixed(1)}%`
        : `Total is ${(Math.round((100 - totals.rounded) * 10) / 10).toFixed(1)}% short of 100%`;

    return (
        <div className="space-y-6">
            <div className="bg-[#f5f5f5] flex flex-col gap-6 rounded-xl border py-6 shadow-sm">
                <div className="grid auto-rows-min items-start gap-1.5 px-6">
                    <div className="flex items-center justify-between">
                        <div className="leading-none font-semibold">
                            Monthly Allocation
                        </div>
                        <div className="flex items-center gap-2">
                            <span className={`inline-flex items-center justify-center rounded-md border px-2 py-0.5 text-xs font-medium w-fit whitespace-nowrap shrink-0 gap-1 overflow-hidden border-transparent ${totals.rounded > 100 ? "bg-red-100 text-red-800" : totals.rounded < 100 ? "bg-yellow-100 text-yellow-800" : "bg-green-100 text-green-800"}`}>
                                Total: {(
                                    Math.round(
                                        Object.values(monthlyData || {}).reduce((a, b) => a + Number(b || 0), 0) * 10
                                    ) / 10
                                )}%
                            </span>
                            {isDirty && (
                                <span
                                    className="inline-flex items-center justify-center rounded-md border px-2 py-0.5 text-xs font-medium w-fit whitespace-nowrap shrink-0 gap-1 overflow-hidden text-orange-600 border-orange-200"
                                >
                                    Unsaved Changes
                                </span>
                            )}
                        </div>
                    </div>
                </div>
                <div className="px-6 space-y-4">
                    {showWarning && (
                        <div
                            className={`flex flex-row relative w-full rounded-lg border px-4 py-3 text-sm gap-2 items-start ${totals.rounded > 100 ? "border-red-200 bg-red-50" : "border-yellow-200 bg-yellow-50"}`}
                        >
                            <ExclamationTriangleIcon className="h-5 w-5" />
                            <span
                                className="text-left text-sm"
                            >
                                {warningText}
                            </span>
                        </div>
                    )}
                    {Object.keys(monthlyData || {}).length > 0 && (
                        <div
                            className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4"
                        >      
                            {monthsLowerCase
                                .filter((key) => Object.prototype.hasOwnProperty.call(monthlyData, key))
                                .map((key) => {
                                    const value = monthlyData[key];
                                    const label = monthKeysForecastEnum[key];
                                    const tipId = `allocation-${key}`;
                                    const inputPct = Number(value || 0);
                                    const atCurrentGoal = (Number(annualGoal || 0) * inputPct) / 100;
                                    const tooltipHtml = `<div><div>Your input: ${inputPct.toFixed(2)}%</div><div>At current goal: $${formatPrice(Number(atCurrentGoal || 0))}</div></div>`;
                                    return (
                                        <div className="space-y-2" key={key}>
                                            <label className="text-sm font-medium text-slate-700">
                                                {label}
                                            </label>
                                            <div className="space-y-1">
                                                <div 
                                                    className="relative"
                                                    data-for={tipId}
                                                    data-tip={true}
                                                >
                                                    <input
                                                        value={value === 0 ? 0 : value}
                                                        onChange={handleChange(key)}
                                                        type="number"
                                                        min="0"
                                                        max="100"
                                                        step="0.1"
                                                        className="border-input flex h-9 w-full min-w-0 rounded-md border bg-transparent px-3 py-1 text-base shadow-xs transition-[color,box-shadow] outline-none disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50 md:text-sm pr-8 appearance-none [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none [moz-appearance:textfield] text-right"
                                                    />
                                                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-slate-600">
                                                        %
                                                    </span>
                                                    <ReactTooltip 
                                                        id={tipId} 
                                                        html={true}
                                                        effect='solid'
                                                        className="!z-50"
                                                    >
                                                        {`
                                                            <div class="w-56">
                                                                <div>Your input: ${inputPct.toFixed(2)}%</div>
                                                                <div>At current goal: $${formatPrice(Number(atCurrentGoal || 0))}</div>
                                                            </div>
                                                        `}
                                                    </ReactTooltip>
                                                </div>
                                            </div>
                                        </div>
                                    )
                                })}
                        </div>
                    )}
                    {access === "edit" && (
                        <div className="flex flex-wrap gap-2">
                            <button
                                onClick={(e) => {
                                    e.stopPropagation();
                                    const data = {
                                        "jan": 8.33, "feb": 8.33, "mar": 8.33,
                                        "apr": 8.33, "may": 8.33, "jun": 8.33,
                                        "jul": 8.33, "aug": 8.33, "sep": 8.33,
                                        "oct": 8.33, "nov": 8.33, "dec": 8.37,
                                    };
                                    setMonthlyData(data);
                                }}
                                className="inline-flex items-center justify-center whitespace-nowrap text-sm font-medium transition-all disabled:pointer-events-none disabled:opacity-50 shrink-0 outline-none border bg-background shadow-xs h-8 rounded-md gap-1.5 px-3 hover:bg-black hover:text-white"
                            >
                                Even Split
                            </button>
                            <button
                                onClick={(e) => {
                                    e.stopPropagation();
                                    const data = {
                                        "jan": 5.25, "feb": 5.25, "mar": 5.25,
                                        "apr": 5.25, "may": 5.25, "jun": 8.00,
                                        "jul": 5.25, "aug": 5.25, "sep": 7.00,
                                        "oct": 5.25, "nov": 15.00, "dec": 28.00,
                                    };
                                    setMonthlyData(data);
                                }}
                                className="inline-flex items-center justify-center whitespace-nowrap text-sm font-medium transition-all disabled:pointer-events-none disabled:opacity-50 shrink-0 outline-none border bg-background shadow-xs h-8 rounded-md gap-1.5 px-3 hover:bg-black hover:text-white"
                            >
                                Holiday Heavy
                            </button>
                        </div>
                    )}
                    {access === "edit" && (
                        <div className="flex items-center justify-between pt-2 border-t">
                            <div className="flex gap-2">
                                <button
                                    onClick={() => setMonthlyData(monthsData || {})}
                                    disabled={!isDirty}
                                    className="inline-flex items-center justify-center whitespace-nowrap text-sm font-medium transition-all disabled:pointer-events-none disabled:opacity-50 shrink-0 outline-none border bg-white shadow-xs hover:bg-gray-50 h-8 rounded-md gap-2 px-4 py-1"
                                >
                                    <ArrowPathIcon className="h-5 w-5" />
                                    <span>Reset</span>
                                </button>
                            <button
                                onClick={async () => {
                                    // fire partial save for monthly_allocation
                                    await dispatch(partialUpdateForecastPlanningRequest({
                                        forecastPlanningId,
                                        lastType,
                                        scenarioChanges: { monthly_allocation: { months: monthlyData } },
                                    }));
                                    onSave?.(monthlyData);
                                }}
                                    disabled={!isDirty}
                                    className="inline-flex gap-2 items-center px-4 py-1 shrink-0 text-sm font-medium disabled:pointer-events-none disabled:opacity-50 rounded-md shadow-sm text-white bg-primary hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
                                >
                                    <InboxStackIcon className="h-5 w-5" />
                                    <span>Save Changes</span>
                                </button>
                            </div>
                        </div>
                    )}
                    <div className="bg-green-50 rounded-lg p-3">
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                            <div>
                                <div className="text-slate-700">
                                    Annual Goal
                                </div>
                                <div className="font-semibold">
                                    ${formatPrice(Number(annualGoal || 0))}
                                </div>
                            </div>
                            <div>
                                <div className="text-slate-700">
                                    Total Allocated
                                </div>
                                <div className="font-semibold">
                                    {Number(totalPercentage || 0).toFixed(2)}%
                                </div>
                            </div>
                            <div>
                                <div className="text-slate-700">
                                    Projected Revenue
                                </div>
                                <div className="font-semibold">
                                    ${formatPrice(Number(projectedRevenue || 0))}
                                </div>
                            </div>
                            <div>
                                <div className="text-slate-700">
                                    Status
                                </div>
                                <div className={`font-semibold ${showWarning ? "text-red-700" : "text-green-700"}`}>
                                    {!showWarning ? "Valid" : "Invalid"}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}